import {Box, Card, Container, Flex, Group, Menu, rem, Text, Title, UnstyledButton} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {
  adjustToTimeZone,
  adjustToTimeZoneSafely,
  ServiceEventSearchCriteria,
  serviceEventsSelector,
  useSearchServiceEventsQuery
} from "@ots-frontend/ots-data";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {ServiceEventDataTable} from "../../components/data-table/ServiceEventDataTable/ServiceEventDataTable";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {selectUIServiceEventSearchFilter} from "../../store/entity/service-event/selectors";
import {useOtsUISelector} from "../../hooks/useOtsUISelector";
import {useDispatch} from "react-redux";
import {setUIServiceEventSearchFilter} from "../../store/entity/service-event/actions";
import {useNavigate} from "react-router-dom";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {HasAccess} from "../../components/auth/HasAccess/HasAccess";
import {ServiceEventSearchToolbar} from "../../components/toolbar/ServiceEventSearchToolbar/ServiceEventSearchToolbar";

export type ServiceEventSearchPageProps = {}

export const ServiceEventSearchPage = (props: ServiceEventSearchPageProps) => {
  const {regionId} = useApp();
  const {t} = useTranslation(["common"]);

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const criteria = useOtsUISelector(selectUIServiceEventSearchFilter)

  const selectServiceEvents = useMemo(() => {
    return serviceEventsSelector();
  }, []);

  const searchArg: ServiceEventSearchCriteria | typeof skipToken = useMemo(() => {
    if (regionId && criteria?.fromDttm) {
      return {
        ...criteria,
        fromDttm: adjustToTimeZone(criteria.fromDttm).getTime(),
        toDttm: adjustToTimeZoneSafely(criteria.toDttm)?.getTime(),
        regionIds: [regionId],
      }
    } else {
      return skipToken
    }
  }, [criteria, regionId])

  const {serviceEvents, isFetching, refetch} = useSearchServiceEventsQuery(searchArg, {
    selectFromResult: (result) => ({
      ...result,
      serviceEvents: selectServiceEvents(result, {regionId: regionId}),
      isFetching: result.isFetching,
    })
  });

  const onFilterChange = useCallback((value: {
    fromDate: Date,
    toDate: Date | undefined,
  }) => {

    const fromDate = value.fromDate
    const toDate = value.toDate

    dispatch(setUIServiceEventSearchFilter({
      ...criteria,
      fromDttm: fromDate.getTime(),
      toDttm: toDate?.getTime(),
    }))
  }, [criteria, refetch, dispatch]);

  return (
      <ThemedPage>
        {(classes) => (
            <ResizeableContent>
              {(size) => (
                  <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
                    <Flex direction="column" h="100%" m="sm" gap="sm">
                      <RouteBreadcrumbs/>
                      <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                        <Group position="apart">
                          <Title
                              order={4}>{t("page.entity.serviceevent.search.title", "Search Service Events")}</Title>
                          <Menu
                              width={260}
                              position="bottom-end"
                              transitionProps={{transition: "pop-top-right"}}
                              withinPortal
                          >
                            <Menu.Target>
                              <UnstyledButton>
                                <Group spacing={7}>
                                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                                    {t('common.action.plural', "Actions")}
                                  </Text>
                                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                                </Group>
                              </UnstyledButton>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item onClick={() => refetch()}>
                                {t("actions.refresh", "Refresh", {ns: "common"})}
                              </Menu.Item>
                              <HasAccess permissions={["edit"]}>
                                <>
                                  <Menu.Divider/>
                                  <Menu.Item onClick={() => navigate("form")}>
                                    {t("common.action.new", "New", {ns: "common"})}
                                  </Menu.Item>
                                </>
                              </HasAccess>
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                        <ServiceEventSearchToolbar
                            defaultValues={{
                              fromDate: criteria?.fromDttm ? new Date(criteria?.fromDttm) : undefined,
                              toDate: criteria?.toDttm ? new Date(criteria?.toDttm) : undefined,
                            }}
                            loading={isFetching}
                            onSubmit={onFilterChange}/>
                      </Card>
                      <Box sx={{flex: 1, overflow: 'auto'}}>
                        <ServiceEventDataTable
                            withBorder
                            records={serviceEvents}
                            recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                            fetching={isFetching}/>
                      </Box>
                    </Flex>
                  </Container>
              )}
            </ResizeableContent>
        )}
      </ThemedPage>
  );
};
